import { useMemo, useState } from 'react';
import type { SetStateAction, VoidFunctionComponent } from 'react';
import type { FormatName, FixPlanFormat, IncludeFixPlanFilterOption } from '../../types';
import type { FormatSelection } from '../FormatSelect';
import { FixPlanReportOptionDetails } from '../../../utils';
import type { SelectOption, SetState } from '../../../../../../../types';
import { SidebarLayout } from '../../../../../../layouts';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../../../../../generic/Button';
import FormatSelect from '../FormatSelect';
import styles from '../Filter/Filter.module.less';
import FloatingToolbar from '../../../../../../generic/FloatingToolbar';
import Flex from '../../../../../../generic/Flex';
import { FilterPanel } from '../../../../../../generic';
import IncludeFixPlanCheckboxGroup from './IncludeFixPlanCheckboxGroup';

type FixPlanFilterState = {
  format: FormatName;
  fixPlanOptions: IncludeFixPlanFilterOption[];
};

export type OnApplyForFixPlan = (args: FixPlanFilterState) => void;

type FixPlanFiltersProps = {
  onApply: OnApplyForFixPlan;
  appliedFormat: FormatName | null;
  setIsDirty: SetState<boolean>;
  isApplyButtonDisabled: boolean;
  fixPlanOptions: IncludeFixPlanFilterOption[];
};

const FixPlanFilters: VoidFunctionComponent<FixPlanFiltersProps> = ({
  isApplyButtonDisabled,
  onApply,
  appliedFormat,
  setIsDirty,
  fixPlanOptions: appliedFixPlanOptions,
}: FixPlanFiltersProps) => {
  const pdfFormat: FixPlanFormat = { label: 'PDF', value: 'PDF' };
  const formats: FixPlanFormat[] = [
    pdfFormat,
    { label: 'HTML', value: 'HTML' },
    { label: 'JSON', value: 'JSON' },
  ];
  const [format, setFormat] = useState<FormatSelection>(pdfFormat);

  const initialIncludeCheckboxFixPlanOptionValues = useMemo(
    () =>
      appliedFixPlanOptions
        .map((option) => ({ value: option, label: FixPlanReportOptionDetails[option]?.title })),
    [appliedFixPlanOptions],
  );
  const [includeCheckboxValues, setIncludeCheckboxValues] = useState<SelectOption[]>(
    initialIncludeCheckboxFixPlanOptionValues,
  );

  const fixPlanOptions = includeCheckboxValues.map(
    ({ value }) => value as IncludeFixPlanFilterOption,
  );

  // Add a helper function to wrap any set function to set isDirty to true
  function withSetIsDirty<T>(fn: (value: SetStateAction<T>) => void) {
    return (value: SetStateAction<T>) => {
      setIsDirty(true);
      fn(value);
    };
  }

  const onClickApply = () => {
    setIsDirty(false);
    onApply({ format: format.value, fixPlanOptions });
  };

  const title = (
    <div className={styles.filterTitle}>
      <span>Customization</span>
    </div>
  );

  function discardFormat() {
    if (appliedFormat) {
      setFormat({ label: appliedFormat, value: appliedFormat });
    } else if (formats[0]) {
    // This happens when user has not applied any format.
    // We need to reset it to the first format in the list.
      setFormat(formats[0]);
    }
  }

  return (
    <SidebarLayout.Sidebar>
      <FilterPanel title={title}>
        <FormatSelect format={format} formats={formats} setFormat={withSetIsDirty(setFormat)} />
        <IncludeFixPlanCheckboxGroup
          values={includeCheckboxValues}
          setValues={withSetIsDirty(setIncludeCheckboxValues)}
        />
      </FilterPanel>
      <FloatingToolbar
        shouldShow={!isApplyButtonDisabled}
        text="Your changes have not been applied."
        actions={
          <Flex gap="8px" wrap="wrap">
            <Button onClick={onClickApply} type={BUTTON_TYPE.PRIMARY} size={BUTTON_SIZE.MEDIUM}>
              Apply
            </Button>
            <Button
              onClick={() => {
                setIncludeCheckboxValues(initialIncludeCheckboxFixPlanOptionValues);
                discardFormat();
                setIsDirty(false);
              }}
              type={BUTTON_TYPE.SLATE}
              size={BUTTON_SIZE.MEDIUM}
          >
              Discard
            </Button>
          </Flex>
      }
    />
    </SidebarLayout.Sidebar>
  );
};

export default FixPlanFilters;
